import { Sparkles, CheckCircle, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import heroBackground from "@/assets/hero-background.jpg";
import { scrollToSection } from "@/utils/scrollUtils";
import { useLandingPageContext } from "@/hooks/useLandingPageContext";

const HeroSection = () => {
  const { generateTestId, generateId } = useLandingPageContext();
  return (
    <section id="hero" className="relative overflow-hidden min-h-screen flex items-center pt-20">
      {/* Background Image with optimized loading */}
      <picture className="absolute inset-0 z-0">
        <source
          media="(max-width: 640px)"
          srcSet={heroBackground}
          width="640"
          height="360"
        />
        <source
          media="(max-width: 1024px)"
          srcSet={heroBackground}
          width="1024"
          height="576"
        />
        <img
          src={heroBackground}
          alt="Hero background"
          width={1920}
          height={1080}
          loading="eager"
          decoding="async"
          className="w-full h-full object-cover"
        />
      </picture>
      
      {/* Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/70 z-[1]" />
      
      <div className="container mx-auto px-4 sm:px-6 md:px-8 text-center relative z-10">
        <div className="bg-black/40 backdrop-blur-sm rounded-2xl p-8 sm:p-10 md:p-12 max-w-6xl mx-auto">
        <div className="animate-fade-in-up">
          <Badge className="mb-6 glass-effect text-dark-accent border-dark-accent/30 font-semibold px-6 py-2">
            ✨ Tecnologia Exclusiva AuraCode
          </Badge>
        </div>
        
        {/* LCP Element - No animation for immediate paint */}
        <div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-playfair font-bold mb-6 leading-tight">
            <span className="text-secondary">
              Numerologia Cabalística:
            </span>
            <br />
            <span className="text-white drop-shadow-lg">
              Descubra Sua Missão de Vida e Prosperidade
            </span>
          </h1>
        </div>
        
        <div className="animate-fade-in-up">
          <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-gray-300 mb-8 max-w-4xl mx-auto leading-relaxed text-center">
            <span className="text-secondary font-semibold">Mapa Numerológico Completo</span> com análise do seu nome, personalidade e propósito de vida.
            <br />
            <br className="hidden md:block" />
            Descubra sua <span className="text-secondary font-semibold">missão de vida</span> e desbloqueie caminhos para a <span className="text-secondary font-semibold">prosperidade</span> através da numerologia cabalística.
          </p>
        </div>
        
        <div className="animate-scale-in">
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-8">
            <Button size="lg" className="text-base sm:text-lg px-8 py-4 sm:px-12 sm:py-6 bg-secondary hover:bg-secondary/90 text-black font-bold button-glow shadow-2xl group rounded-xl w-full sm:w-auto" id={generateId('hero', 'gerar-mapa')} data-testid={generateTestId('hero', 'gerar-mapa')} onClick={() => scrollToSection('precos')}>
              <Sparkles className="mr-2 h-5 w-5 sm:h-6 sm:w-6 group-hover:animate-spin-slow transition-transform duration-500" />
              Gerar Meu Mapa Agora
            </Button>
            <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-400">
              <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-dark-accent-green" />
              Entrega Instantânea
            </div>
          </div>
        </div>
        
        <div className="animate-slide-in-left">
          <div className="flex justify-center items-center gap-4 sm:gap-6 md:gap-8 text-xs sm:text-sm text-gray-400 flex-wrap">
            <div className="flex items-center gap-2 glass-effect px-3 py-2 sm:px-4 sm:py-2 rounded-full">
              <div className="flex">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-3 w-3 sm:h-4 sm:w-4 fill-dark-accent text-dark-accent" />
                ))}
              </div>
              <span className="font-medium text-white text-xs sm:text-sm">4.9/5 (2.847 avaliações)</span>
            </div>
            <div className="flex items-center gap-2 glass-effect px-3 py-2 sm:px-4 sm:py-2 rounded-full">
              <div className="w-2 h-2 bg-dark-accent rounded-full animate-pulse" />
              <span className="text-white text-xs sm:text-sm">+5.200 mapas gerados</span>
            </div>
          </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;